import { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, Card, CardContent,
    CardMedia, IconButton, Tooltip, Chip, Pagination,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


/**
 * Pending review-foto moderācijas lapa. Atspoguļo visus foto, kurus citi lietotāji
 * ir pievienojuši kā atsauksmes pielikumus un kuriem accepted ir NULL.
 *
 * Server endpoint'i (AdminModerationController.cs):
 *   POST /api/v1/admin/moderation/photos/pending  - saraksta lapa ar pagination
 *   POST /api/v1/admin/moderation/photos/decide   - accept/reject; serveris arī izsūta push autoram
 *
 * Foto baits (/api/v1/places/image/{id}) prasa X-Client-Token, tāpēc <img src=> tiešā veidā
 * neder - lādējam caur axios kā blob un veidojam object URL (skat. [ModerationImageThumb]).
 */
type PendingItem = {
    imageId: string;
    placeId: string;
    url: string;
    shortDescription: string | null;
    reviewUserId: string;
    placeAddress: string | null;
    uploadedAt: string;
};

type PendingResponse = {
    isSuccess: boolean;
    error: string | null;
    totalPending: number;
    items: PendingItem[];
};


const PAGE_SIZE = 20;


export function PhotosPage() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [zoomImageId, setZoomImageId] = useState<string | null>(null);
    const [busyImageId, setBusyImageId] = useState<string | null>(null);

    const skip = (page - 1) * PAGE_SIZE;

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['admin-photo-moderation', skip],
        queryFn: async () => {
            const resp = await api.post<PendingResponse>(
                '/api/v1/admin/moderation/photos/pending',
                { skip, take: PAGE_SIZE },
            );
            return resp.data;
        },
        // 30s, lai nepieciešamības gadījumā divi paralēli admin'i nesastrādātos nepatīkami
        staleTime: 30_000,
    });

    const decideMutation = useMutation({
        mutationFn: async (args: { placeId: string; imageId: string; accepted: boolean }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/moderation/photos/decide',
                args,
            );
            if (!resp.data.isSuccess) {
                throw new Error(resp.data.error || 'Lēmums neizdevās.');
            }
            return args;
        },
        onMutate: (args) => setBusyImageId(args.imageId),
        onSettled: () => setBusyImageId(null),
        onSuccess: () => {
            // Pēc lēmuma serveris noņem foto no pending kopas. Lai ielādētu nākamo lapu ar
            // jaunajiem skaitļiem un izņemtu jau apstrādāto rindu, invalidējam visu key.
            queryClient.invalidateQueries({ queryKey: ['admin-photo-moderation'] });
        },
    });

    const items = data?.items ?? [];
    const totalPending = data?.totalPending ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalPending / PAGE_SIZE));

    // Ja pēc lēmumu pieņemšanas saraksts izprasīto lapu padara tukšu (visus apstiprināji), atgriezies vienu nost
    useEffect(() => {
        if (!isLoading && items.length === 0 && page > 1) {
            setPage((p) => Math.max(1, p - 1));
        }
    }, [items.length, isLoading, page]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Foto moderācija</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Atsauksmju pielikumi, kas gaida lēmumu. Apstiprināti foto kļūst publiski redzami;
                        noraidītie paliek DB ar accepted=false un netiek rādīti. Pēc lēmuma autoram tiek
                        nosūtīts push paziņojums.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                        label={`Kopā gaida: ${totalPending}`}
                        color={totalPending > 0 ? 'warning' : 'default'}
                    />
                    <Tooltip title="Atjaunot">
                        <span>
                            <IconButton
                                onClick={() => refetch()}
                                disabled={isFetching}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt moderācijas rindu: {String((error as Error).message)}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && items.length === 0 && (
                <Alert severity="success">
                    Šobrīd nav nekā, ko moderēt. Visi atsauksmju pielikumi ir apstrādāti.
                </Alert>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)',
                    },
                }}
            >
                {items.map((it) => (
                    <Card key={it.imageId}>
                        <ModerationImageThumb
                            imageId={it.imageId}
                            onZoomClick={() => setZoomImageId(it.imageId)}
                        />
                        <CardContent>
                            <Stack spacing={0.5}>
                                <Typography variant="body2" noWrap title={it.placeAddress ?? ''}>
                                    {it.placeAddress ?? '(adrese nezināma)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Augšupielādēts {new Date(it.uploadedAt).toLocaleString('lv-LV')}
                                </Typography>
                                <Tooltip title={it.reviewUserId}>
                                    <Typography
                                        variant="caption" color="text.secondary"
                                        sx={{ fontFamily: 'monospace' }}
                                        noWrap
                                    >
                                        Autors: {it.reviewUserId}
                                    </Typography>
                                </Tooltip>
                            </Stack>
                            <Stack direction="row" spacing={1} mt={1.5}>
                                <Button
                                    fullWidth
                                    color="success"
                                    variant="contained"
                                    startIcon={<CheckCircleOutlineIcon />}
                                    disabled={busyImageId === it.imageId}
                                    onClick={() => decideMutation.mutate({
                                        placeId: it.placeId,
                                        imageId: it.imageId,
                                        accepted: true,
                                    })}
                                >
                                    Apstiprināt
                                </Button>
                                <Button
                                    fullWidth
                                    color="error"
                                    variant="outlined"
                                    startIcon={<HighlightOffIcon />}
                                    disabled={busyImageId === it.imageId}
                                    onClick={() => decideMutation.mutate({
                                        placeId: it.placeId,
                                        imageId: it.imageId,
                                        accepted: false,
                                    })}
                                >
                                    Noraidīt
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        page={page}
                        count={pageCount}
                        onChange={(_, p) => setPage(p)}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {zoomImageId && (
                <ZoomDialog
                    imageId={zoomImageId}
                    onClose={() => setZoomImageId(null)}
                />
            )}
        </Box>
    );
}


/**
 * Foto miniatūra ar zoom pogu. Servera /api/v1/places/image/{id} endpoint anonīmiem pieslēgšanās
 * neprasa, bet PRASA X-Client-Token galvenes, ko <img src=> tiešā veidā nepiesien. Tāpēc
 * lādējam caur axios kā blob un veidojam ObjectURL, ko atbrīvojam pie un-mount.
 */
function ModerationImageThumb({
    imageId, onZoomClick,
}: { imageId: string; onZoomClick: () => void }) {
    const { data: objectUrl, isLoading } = useQuery({
        queryKey: ['admin-photo-blob', imageId],
        queryFn: async () => {
            const resp = await api.get(`/api/v1/places/image/${imageId}`, {
                responseType: 'blob',
            });
            return URL.createObjectURL(resp.data as Blob);
        },
        staleTime: Infinity,
        gcTime: 5 * 60_000,
    });

    useEffect(() => {
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [objectUrl]);

    return (
        <Box sx={{ position: 'relative', bgcolor: '#0001' }}>
            {isLoading ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <CardMedia
                    component="img"
                    image={objectUrl}
                    alt="Pending review photo"
                    sx={{ height: 220, objectFit: 'cover' }}
                />
            )}
            <Tooltip title="Atvērt pilnekrānā">
                <IconButton
                    size="small"
                    onClick={onZoomClick}
                    sx={{
                        position: 'absolute', top: 4, right: 4,
                        bgcolor: 'rgba(0,0,0,0.45)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                    }}
                >
                    <ZoomInIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}


/**
 * Pilnekrāna preview dialog - lādē to pašu blob lielākā izmērā. Pārvalda paši ObjectURL
 * dzīves ciklu, lai izvairītos no lielas atmiņas patēriņa, ja admin caurskata daudz foto.
 */
function ZoomDialog({ imageId, onClose }: { imageId: string; onClose: () => void }) {
    const { data: objectUrl } = useQuery({
        queryKey: ['admin-photo-blob', imageId],
        queryFn: async () => {
            const resp = await api.get(`/api/v1/places/image/${imageId}`, { responseType: 'blob' });
            return URL.createObjectURL(resp.data as Blob);
        },
        staleTime: Infinity,
    });

    return (
        <Box
            onClick={onClose}
            sx={{
                position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.85)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1500, cursor: 'zoom-out', p: 4,
            }}
        >
            {objectUrl
                ? <img
                      src={objectUrl}
                      alt="Pending review photo (preview)"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                : <CircularProgress sx={{ color: 'white' }} />
            }
        </Box>
    );
}
